import { useCountdown } from 'usehooks-ts'
import clsx from 'clsx'
import { HallType } from '@weblite/ts-action/hall'
import { Timestamp } from '@weblite-wapps/protocol'
import { CallType } from '@weblite/ts-action/media-proxy'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { LiveTribuneIcon, MeetIcon, TribuneIcon } from '@weblite/icons'
import { styled, Typography, ButtonBase, CircularProgress } from '@mui/material'
import { useOpenedRoomId } from '$logic/opened-room'
import { t } from '$i18n'
import { SCHEDULED_CALL_BAR_HEIGHT } from '$comp/_app/components/chat/components/conf'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import {
  calculateDateMetaFromSeconds,
  calculateDatesDiff,
  formatToLocaleDateTime,
} from '$helper/functions/date'
import { toLocale } from '$helper/functions/text'
import { callService, useJoinCallWithTimeout } from '$logic/call'
import { useRoom } from '$logic/room'
import {
  HallCallType,
  showChooseHallCallTimeDialog,
} from '$comp/modals/create-hall-call/choose-call-time'
import { DotPulseLoading } from '$common/loading/dot-pulse-loading/dot-pulse-loading'
import { ActiveAudioWaves } from '$common/active-audio-waves'
import { TribuneActiveAudioWaves } from '$common/tribune-active-audio-waves'
import { LiveActiveIcon } from '$common/live-active-icon/live-active-icon'
import {
  showDeleteScheduledCallConfirmationModal,
  showStartScheduledCallDialog,
} from '$comp/modals/confirmation-dialog'
import { showErrorSnackbar } from '$comp/snackbars'
import { useTimeout } from '$hooks/use-timeout'
import { dateWithOffset } from '$setup/time-offset'

const ACTION_BUTTON_MAX_WIDTH = 116
const ROOT_PADDING_RIGHT = 16
const ROOT_PADDING_LEFT = 12
const CALL_TYPE_ICON_SIZE = 20
const CALL_INFO_HEADER_GAP = 6

const JOIN_CALL_TIMEOUT = 5000

export function HallScheduledCallBar({
  callType,
  title,
  callTime,
  isCallStarted: isCallStartedDefault = false,
}: {
  callType: CallType
  title?: string
  callTime: Timestamp
  isCallStarted?: boolean
}) {
  const { joinCall, joiningCall } = useJoinCallWithTimeout()
  const [countdown, setCountDown] = useState<number | null>(null)
  const openedRoomId = useOpenedRoomId()
  const room = useRoom(openedRoomId || '')
  const userPermissions = useClientUserPermissions(openedRoomId || '')
  const isAdmin = userPermissions?.isAdmin || userPermissions?.isOwner
  const [isCallStarted, setIsCallStarted] = useState(isCallStartedDefault)
  const showActionButton = isAdmin || isCallStarted

  const handleJoinCall = useCallback(
    async (getConfirmation = true) => {
      if (!room || !openedRoomId || joiningCall) return

      try {
        if (getConfirmation) await showStartScheduledCallDialog(callType)

        joinCall(openedRoomId, room.type, callType)
      } catch {
        //
      }
    },
    [openedRoomId, room, joiningCall, callType, joinCall],
  )

  const handleClickBackgroundButton = useCallback(async () => {
    if (!room || joiningCall) return
    if (isCallStarted) {
      handleJoinCall(false)
      return
    }
    if (!isAdmin) return
    const newCallTime = await showChooseHallCallTimeDialog({
      callType: callType as HallCallType,
      hallType: room?.type as HallType,
      defaultTitle: title,
      defaultTime: new Date(callTime),
      editMode: true,
    })
    if (newCallTime === 'DELETE') {
      try {
        await showDeleteScheduledCallConfirmationModal(callType)
        callService.unscheduleCall(room.id, room.type, callType)
        return
      } catch {
        return
      }
    }
    const {
      time,
      title: newTitle,
      shouldStartNow,
      shouldSendNotificationMessage,
    } = newCallTime
    if (shouldStartNow) {
      handleJoinCall(false)
      return
    }
    callService.updateScheduledCall(
      room.id,
      room.type,
      callType,
      shouldSendNotificationMessage,
      title,
      callTime,
      newTitle,
      +time,
    )
  }, [handleJoinCall, room, joiningCall, isCallStarted, callType])

  useEffect(() => {
    setIsCallStarted(isCallStartedDefault)
  }, [isCallStartedDefault])

  useEffect(() => {
    if (isCallStartedDefault) return
    const { days } = calculateDatesDiff(new Date(), new Date(callTime))
    if (days === 0) {
      setCountDown(Math.round(Math.abs(callTime - dateWithOffset()) / 1000))
    } else {
      setCountDown(null)
    }
  }, [callTime, isCallStartedDefault])

  if (
    callType !== 'tribune' &&
    callType !== 'live' &&
    callType !== 'group_call'
  )
    return null

  return (
    <Root>
      <BackgroundButton
        onClick={handleClickBackgroundButton}
        className={clsx({
          tribune: callType === 'tribune',
          live: callType === 'live',
          meeting: callType === 'group_call',
        })}
      >
        <CallInfoHeader
          sx={{
            maxWidth: `calc(100% - ${
              (showActionButton ? ACTION_BUTTON_MAX_WIDTH : 0) +
              ROOT_PADDING_LEFT +
              ROOT_PADDING_RIGHT
            }px)`,
          }}
        >
          <IconSelector isCallStarted={isCallStarted} callType={callType} />
          <CallInfoTitle
            sx={{
              width: `calc(100% - ${
                CALL_TYPE_ICON_SIZE + CALL_INFO_HEADER_GAP
              }px)`,
            }}
          >
            <Typography
              variant="subtitle3"
              color="neutral900"
              noWrap
              maxWidth="100%"
            >
              {title || t(`call.${callType}`)}
              {!title && callType === 'tribune' && (
                <Typography
                  variant="caption1"
                  color="neutral700"
                  component="span"
                >
                  {` (${t('call.voiceCall')})`}
                </Typography>
              )}
            </Typography>
            {isCallStarted && (
              <Typography
                sx={{ display: 'flex', alignItems: 'center' }}
                variant="caption1"
                color="success700"
              >
                {t('call.inProgress')}{' '}
                <DotPulseLoading
                  size={5}
                  sx={{ color: 'success700', mr: '4px' }}
                />
              </Typography>
            )}
          </CallInfoTitle>
        </CallInfoHeader>
        {!isCallStarted && (
          <Typography
            sx={{ display: 'flex', alignItems: 'center' }}
            variant="caption1"
            color="neutral700"
          >
            {countdown !== null ? (
              <>
                <Typography variant="inherit" color="inherit">
                  {t('call.startsFrom')}
                </Typography>
                <StartsFrom
                  seconds={countdown}
                  onCallStarted={() => setIsCallStarted(true)}
                />
              </>
            ) : (
              `${t('call.startsAtDate')} ${formatToLocaleDateTime(
                new Date(callTime),
              )}`
            )}
          </Typography>
        )}
      </BackgroundButton>
      {showActionButton && (
        <ActionButton
          className={clsx({
            tribune: callType === 'tribune',
            live: callType === 'live',
            meeting: callType === 'group_call',
            disabled: joiningCall,
          })}
          disabled={joiningCall}
          onClick={() => {
            handleJoinCall(!isCallStarted)
          }}
        >
          {/* {!isAdmin &&
            (isNotificationActive ? (
              <AlarmAddIcon
                sx={{ color: 'inherit', width: '20px', height: '20px' }}
              />
            ) : (
              <AlarmOnIcon
                sx={{ color: 'inherit', width: '20px', height: '20px' }}
              />
            ))} */}
          {joiningCall ? (
            <CircularProgress color="inherit" size={20} />
          ) : (
            <Typography variant="subtitle4" color="inherit" zIndex={1}>
              {isCallStarted
                ? callType === 'group_call'
                  ? t('call.join')
                  : t('call.watch')
                : t('call.startNow')}
            </Typography>
          )}
        </ActionButton>
      )}
    </Root>
  )
}

const StartsFrom = ({
  seconds,
  onCallStarted,
}: {
  seconds: number
  onCallStarted: () => void
}) => {
  const [timeLeft, { startCountdown, resetCountdown }] = useCountdown({
    countStart: seconds,
    intervalMs: 1000,
  })

  useEffect(() => {
    resetCountdown()
    startCountdown()
  }, [seconds])

  useEffect(() => {
    if (timeLeft === 0) {
      onCallStarted()
    }
  }, [timeLeft])

  const {
    seconds: s,
    minutes: m,
    hours: h,
  } = useMemo(() => {
    return calculateDateMetaFromSeconds(timeLeft)
  }, [timeLeft])

  return (
    <Typography
      variant="caption1"
      color="success700"
      dir="ltr"
      mr="3px"
      sx={{ display: 'flex', alignItems: 'center' }}
    >
      <Typography width="20px" variant="inherit" color="inherit">
        {`${toLocale(h, false, 2)} `}
      </Typography>
      <Typography variant="inherit" color="neutral700">
        :
      </Typography>
      <Typography width="20px" variant="inherit" color="inherit">
        {` ${toLocale(m, false, 2)} `}
      </Typography>
      <Typography variant="inherit" color="neutral700">
        :
      </Typography>
      <Typography width="20px" variant="inherit" color="inherit">
        {` ${toLocale(s, false, 2)}`}
      </Typography>
    </Typography>
  )
}

const IconSelector = ({
  callType,
  isCallStarted,
}: {
  callType: CallType
  isCallStarted: boolean
}) => {
  const sharedSx = { width: CALL_TYPE_ICON_SIZE, height: CALL_TYPE_ICON_SIZE }

  if (!isCallStarted) {
    if (callType === 'tribune')
      return <TribuneIcon sx={{ ...sharedSx, color: 'tribune500' }} />
    if (callType === 'live')
      return <LiveTribuneIcon sx={{ ...sharedSx, color: 'live500' }} />
    if (callType === 'group_call')
      return <MeetIcon sx={{ ...sharedSx, color: 'primary500' }} />
  } else {
    if (callType === 'group_call') return <ActiveAudioWaves />
    if (callType === 'live') return <LiveActiveIcon />
    if (callType === 'tribune') return <TribuneActiveAudioWaves />
  }

  return null
}

const Root = styled('div')`
  width: 100%;
  height: ${SCHEDULED_CALL_BAR_HEIGHT}px;
  position: relative;
`

const BackgroundButton = styled(ButtonBase)`
  align-items: flex-start;
  position: relative;
  flex-direction: column;
  gap: 6px;
  overflow: hidden;

  width: 100%;
  height: 100%;
  padding: 0 ${ROOT_PADDING_RIGHT}px 0 ${ROOT_PADDING_LEFT}px;
  overflow: hidden;
  background: linear-gradient(270deg, #ffe0ed 0%, #ffa3c9 100%);
  &.tribune {
    background: ${({ theme }) => theme.palette.tribuneGradientL};
  }
  &.live {
    background: ${({ theme }) => theme.palette.liveGradientL};
  }
  &.meeting {
    background: ${({ theme }) => theme.palette.meetingGradientL};
  }
`

const CallInfoHeader = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: ${CALL_INFO_HEADER_GAP}px;
`

const CallInfoTitle = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`

const ActionButton = styled(ButtonBase)`
  max-width: ${ACTION_BUTTON_MAX_WIDTH}px;
  height: 32px;
  min-width: 100px;
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 7px 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 12px;
  color: ${({ theme }) => theme.palette.neutral000};
  background: linear-gradient(180deg, #7c63f8 0%, #5454f7 100%);
  z-index: 1;
  &.tribune {
    background: ${({ theme }) => theme.palette.tribuneGradientH};
  }
  &.meeting {
    background: ${({ theme }) => theme.palette.meetingGradientH};
  }
  &.live {
    background: ${({ theme }) => theme.palette.liveGradientH};
  }
  &[data-is-admin='false'] {
    background: ${({ theme }) => theme.palette.neutral600};
    &[data-is-notification-active='false'] {
      &[data-call-type='tribune'] {
        background: ${({ theme }) => theme.palette.tribuneGradientH};
      }
      &[data-call-type='live'] {
        background: ${({ theme }) => theme.palette.liveGradientH};
      }
      &[data-call-type='group_call'] {
        background: ${({ theme }) => theme.palette.meetingGradientH};
      }
    }
  }
  &.disabled {
    opacity: 0.8;
  }
`
